import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Container,
    Divider,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    LocalHospital,
    Visibility,
    VisibilityOff,
    CheckCircle,
    Cancel,
} from '@mui/icons-material';
import { Trans } from 'react-i18next';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';

interface PasswordRequirement {
    label: string;
    met: boolean;
}

export const SignupPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAuth = useAuthStore((state) => state.setAuth);
    const setCurrentVet = useAuthStore((state) => state.setCurrentVet);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phone: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    // Password strength checker
    const passwordRequirements: PasswordRequirement[] = [
        { label: t('auth.signup.passwordMin8'), met: formData.password.length >= 8 },
        { label: t('auth.signup.passwordUppercase'), met: /[A-Z]/.test(formData.password) },
        { label: t('auth.signup.passwordLowercase'), met: /[a-z]/.test(formData.password) },
        { label: t('auth.signup.passwordNumber'), met: /\d/.test(formData.password) },
    ];

    const isPasswordValid = passwordRequirements.every((req) => req.met);
    const passwordsMatch = formData.password === formData.confirmPassword;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
            setError(t('auth.signup.fillAllFields'));
            return;
        }

        if (!isPasswordValid) {
            setError(t('auth.signup.passwordRequirements'));
            return;
        }

        if (!passwordsMatch) {
            setError(t('auth.signup.passwordMismatch'));
            return;
        }

        if (!acceptedTerms) {
            setError(t('auth.signup.acceptTermsRequired'));
            return;
        }

        setLoading(true);

        try {
            await authService.register({
                email: formData.email,
                password: formData.password,
                first_name: formData.firstName,
                last_name: formData.lastName,
                phone: formData.phone || undefined,
            });

            setSuccess(t('auth.signup.success'));

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err: any) {
            const detail = err.response?.data?.detail;
            if (typeof detail === 'string') {
                setError(detail);
            } else if (Array.isArray(detail)) {
                setError(detail[0]?.msg || t('auth.signup.error'));
            } else {
                setError(t('auth.signup.error'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignupSuccess = async (credentialResponse: CredentialResponse) => {
        try {
            setLoading(true);
            if (credentialResponse.credential) {
                // Google Sign Up is effectively a login (or registration + login)
                const response = await authService.googleLogin(credentialResponse.credential);

                setAuth(
                    {
                        ...response.user,
                        role: response.user.role as any,
                        is_active: true,
                        created_at: new Date().toISOString(),
                    },
                    response.access_token,
                    response.refresh_token
                );

                setCurrentVet(
                    response.user.id,
                    `Dr. ${response.user.first_name} ${response.user.last_name}`
                );

                navigate('/dashboard');
            }
        } catch (error) {
            console.error('Google Signup Error:', error);
            setError(t('auth.signup.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleError = () => {
        console.error('Google Signup Failed');
        setError(t('auth.signup.error'));
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <LocalHospital sx={{ fontSize: 48, color: 'primary.main' }} />
                    </Box>

                    <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                        {t('auth.signup.title')}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                        {t('auth.signup.subtitle')}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            {success}
                        </Alert>
                    )}

                    {/* Google OAuth Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}>
                        <GoogleLogin
                            onSuccess={handleGoogleSignupSuccess}
                            onError={handleGoogleError}
                            useOneTap
                            theme="outline"
                            size="large"
                            width="100%"
                            text="signup_with"
                        />
                    </Box>

                    <Divider sx={{ my: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.signup.or')}
                        </Typography>
                    </Divider>

                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="firstName"
                                label={t('auth.signup.firstName')}
                                name="firstName"
                                autoComplete="given-name"
                                autoFocus
                                value={formData.firstName}
                                onChange={handleChange}
                                disabled={loading}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="lastName"
                                label={t('auth.signup.lastName')}
                                name="lastName"
                                autoComplete="family-name"
                                value={formData.lastName}
                                onChange={handleChange}
                                disabled={loading}
                            />
                        </Box>

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label={t('auth.signup.email')}
                            name="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            fullWidth
                            id="phone"
                            label={t('auth.signup.phone')}
                            name="phone"
                            autoComplete="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label={t('auth.signup.password')}
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="new-password"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Password requirements */}
                        {formData.password && (
                            <Box sx={{ mt: 1, mb: 2 }}>
                                {passwordRequirements.map((req, index) => (
                                    <Box
                                        key={index}
                                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                    >
                                        {req.met ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <Cancel sx={{ fontSize: 16, color: 'error.main' }} />
                                        )}
                                        <Typography
                                            variant="caption"
                                            color={req.met ? 'success.main' : 'error.main'}
                                        >
                                            {req.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label={t('auth.signup.confirmPassword')}
                            type={showPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            autoComplete="new-password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            disabled={loading}
                            error={formData.confirmPassword !== '' && !passwordsMatch}
                            helperText={
                                formData.confirmPassword !== '' && !passwordsMatch
                                    ? t('auth.signup.passwordMismatch')
                                    : ''
                            }
                        />

                        <FormControlLabel
                            sx={{ mt: 2, alignItems: 'flex-start' }}
                            control={
                                <Checkbox
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                    disabled={loading}
                                    size="small"
                                    sx={{ pt: 0.5 }}
                                />
                            }
                            label={
                                <Typography variant="body2" color="text.secondary">
                                    <Trans
                                        i18nKey="auth.signup.acceptTerms"
                                        components={[
                                            <a
                                                href="/cgu"
                                                style={{ color: 'inherit', fontWeight: 600 }}
                                            />,
                                        ]}
                                    />
                                </Typography>
                            }
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 2, mb: 2 }}
                            disabled={loading || !isPasswordValid || !passwordsMatch || !acceptedTerms}
                        >
                            {loading ? <CircularProgress size={24} /> : t('auth.signup.submit')}
                        </Button>

                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2">
                                {t('auth.signup.haveAccount')}{' '}
                                <Link
                                    to="/login"
                                    style={{ color: 'inherit', fontWeight: 600 }}
                                >
                                    {t('auth.signup.login')}
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};
